/** 
 * @module SEEMPMonitoringPlanIII
 * @description SEEMP III Monitoring Plan component for generating templated plan.
 * This is a child component branched out from PlanGenerator.js to handle only SEEMP III Monitoring Plans
 */
import { useState, useEffect, useContext } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import "./SEEMPMonitoringPlan.scss";

import ReviewAndUpdateLog from "./ReviewAndUpdateLog/ReviewAndUpdateLog";
import ShipParticularsIII from "./ShipParticularsIII/ShipParticularsIII";
import CiiCalculationMethodology from "./CiiCalculationMethodology/CiiCalculationMethodology";
import ThreeyearImplementation from "./ThreeyearImplementation/ThreeyearImplementation";
import SelfEvaluationImprovement from "./Selfevaluationimprovement/Selfevaluationimprovement";
import PlanOfCorrectiveActions from "./PlanOfCorrectiveActions/PlanOfCorrectiveActions";
import CauseAnalysisCiiRating from "./CauseAnalysisCiiRating/CauseAnalysisCiiRating";
import CorrectiveActionAnalysis from "./CorrectiveActionAnalysis/CorrectiveActionAnalysis";
import CiiTargetsActuals from "./CiiTargetsActuals/CiiTargetsActuals";
import CiiTargets from "./CiiTargets/CiiTargets";
import ThreeYearImplementationPlan from "./ThreeYearImplementationplan/ThreeYearImplementationplan";
import ListMeasures from "./ListMeasures/ListMeasures";
import CiiImpactCalculation from "./CiiImpactCalculation/CiiImpactCalculation";
import CiiRating from "./CiiRating/CiiRating";
import MeasureImplementationplan from "./MeasureImplementationplan/MeasureImplementationplan";
import Appendix from "./Appendix/Appendix";
import AdditionalAndRevisedMeasures from "./AdditionalAndRevisedMeasures/AdditionalAndRevisedMeasures";
import ListMeasuresTable from "./ListMeasures/ListMeasuresTable";
import AdditionalAndRevisedMeasuresTable from "./AdditionalAndRevisedMeasures/AdditionalAndRevisedMeasuresTable";

import { callApi, getVesselById } from "../../../../services/MonitoringPlanService";
import { API_ROUTES } from "../ApiRoutes/ApiRoutes";
import axiosHttp from "../../../../services/interceptor";
import { Button, Icon, ICONS } from "abs-ui-components";
import { emitToasterMsg } from "../../../../services/toasterUtility";
import { ClaimsContext } from "../../../../services/ClaimsService";
import { FeatureType } from "../../../../constants/FeatureToggles";
import CloneModal from "../../CloneModal";
import ResultSummaryModal from "../../ResultSummaryModal";

/**
 * @param {object} props
 * @param {number|string} props.vesselMstId
 * @param {string} props.status
 * @param {object} props.planVerificationDetails
 * @param {object} props.planVerificationFlags
 * @param {string} props.wcnNumber
 * @param {Function} props.refreshState
 */
const SeempMonitoringPlanIII = ({
  vesselMstId,
  status,
  planVerificationDetails,
  planVerificationFlags,
  wcnNumber,
  refreshState
}) => {
  /* ---------------------------- State ---------------------------- */

  const [expandedMainPanel, setExpandedMainPanel] = useState("panel1");
  const [expandedChild, setExpandedChild] = useState(null);
  const [monitoringData, setMonitoringData] = useState();
  const [allDropdownData, setAllDropdownData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [vesselData, setVesselData] = useState(null);
  const [showCloneModal, setShowCloneModal] = useState(false);

  const [selectedSections, setSelectedSections] = useState([]);
  const [selectedVessels, setSelectedVessels] = useState([]);
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const { userHasFeature } = useContext(ClaimsContext);
  const [cloneType, setCloneType] = useState("");
  const [sectionsList, setSectionsList] = useState([]);
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultSummaryData, setResultSummaryData] = useState([]);


  /* ----------------------- Permissions --------------------------- */

  const canSubmitDocuments =
    planVerificationDetails?.VerificationStatus === "Draft" ||
    planVerificationDetails?.VerificationStatus === "Retained" ||
    (planVerificationDetails?.VerificationStatus === "Under Review" &&
      planVerificationFlags?.data?.hasCorrections);

  /* ------------------------- Form setup -------------------------- */

  const methods = useForm({
    mode: "onBlur",
    defaultValues: { NameOfTheShip: "" }
  });

  /* ------------------------- UI Helpers -------------------------- */

  const renderTitleWithIcon = (panel, title) => (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <span style={{ fontSize: "20px", fontWeight: "bold" }}>
        {expandedMainPanel === panel || expandedChild === panel ? "−" : "+"}
      </span>
      <p className="fuel-cons">{title}</p>
    </div>
  );

  const handleMainChange = (panel) => (_, isExpanded) => {
    setExpandedMainPanel(isExpanded ? panel : false);
    if (panel !== "parentPanel") setExpandedChild(null);
  };
  const handleChildChange = (panel) => (_, isExpanded) => {
    setExpandedChild(isExpanded ? panel : null);
  };

  /* ---------------------- Data Fetchers -------------------------- */

  const fetchMonitoringDropdownData = async () => {
    try {
      const dropdownData = await callApi({
        method: "GET",
        url: API_ROUTES.GET_ALL_SEEMPIII_DROPDOWN_DATA(),
        data: {}
      });
      if (dropdownData) setAllDropdownData(dropdownData);
    } catch (error) {
      console.error("Error fetching dropdown data", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMonitoringData = async () => {
    try {
      const response = await callApi({
        method: "GET",
        url: API_ROUTES.GET_SEEMP_MONITORINGPLAN_III_BY_VESSELID(vesselMstId),
        data: {}
      });
      if (response) setMonitoringData(response);
    } catch (error) {
      console.error("Error fetching monitoring plan data", error);
    } finally {
      setLoading(false);
    }
  };

  const refetchMonitoringData = async () => {
    try {
      const response = await callApi({
        method: "GET",
        url: API_ROUTES.GET_SEEMP_MONITORINGPLAN_III_BY_VESSELID(vesselMstId),
        data: {}
      });
      if (response) setMonitoringData(response);
    } catch (error) {
      console.error("Error refetching monitoring plan data", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVesselData = async () => {
    try {
      const response = await getVesselById(vesselMstId);
      if (response) setVesselData(response);
    } catch (error) {
      console.error("Error fetching vessel data", error);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------- Effects ------------------------------- */

  useEffect(() => {
    fetchMonitoringDropdownData();
  }, []);

  useEffect(() => {
    if (vesselMstId) fetchMonitoringData();
  }, [vesselMstId]);

  useEffect(() => {
    if (vesselMstId) {
      refetchMonitoringData();
      fetchVesselData();
    }
  }, [vesselMstId]);

  /* ------------------ Validation Wrapper ------------------------- */

  const validateAndExecute = async (apiUrl, onSuccess, errorTitle) => {
    if (!vesselMstId) return;
    try {
      const res = await callApi({ method: "GET", url: apiUrl, data: {} });
      if (res?.isValid) {
        await onSuccess();
      } else {
        emitToasterMsg("error", errorTitle, res?.errors || "Validation failed");
      }
    } catch (err) {
      emitToasterMsg("error", errorTitle, err?.errors || "Something went wrong");
    }
  };

  /* ------------------------- Actions ----------------------------- */

  const handleDownloadPDF = async () => {
    try {
      const res = await callApi({
        method: "POST",
        url: `${API_ROUTES.GET_SEEMP_DOWNLOAD_PDF(vesselMstId)}?planType=seempiii`,
        data: {},
        responseType: "blob"
      });
      if (res) {
        const url = window.URL.createObjectURL(new Blob([res], { type: "application/pdf" }));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `SEEMP III plan ${vesselData.ImoNumber}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        emitToasterMsg("success", "PDF", "PDF downloaded successfully");
      }
    } catch (err) {
      console.error("Error downloading PDF", err);
      emitToasterMsg("error", "PDF", "Failed to download PDF");
    }
  };

  const handleSubmitSeempIIIMonitoringPlan = async () => {
    try {
      setIsSubmitting(true);
      const planId = planVerificationDetails?.PlanId;
      if (planId) {
        await axiosHttp({
          url: `api/PlanVerification/SubmitToDocuments?planId=${encodeURIComponent(planId)}&wcnNumber=${encodeURIComponent(wcnNumber)}`,
          method: "GET"
        });
        emitToasterMsg("success", "Documents", "Submitted to Documents");
      } else {
        emitToasterMsg("error", "Documents", "Failed to submit to Documents, could not determine PlanId");
      }
    } catch (err) {
      console.error("Error calling SubmitToDocuments", err);
      emitToasterMsg("error", "Documents", "Failed to submit to Documents");
    } finally {
      setIsSubmitting(false);
      refreshState?.();
    }
  };

  const handleValidate = async (planType) => {
    try {
      const res = await callApi({
        method: "POST",
        url: API_ROUTES.Validate_Plan(planType, vesselMstId),
        data: {}
      });

      if (res?.isValid) {
        await fetchSections(planType);
        emitToasterMsg("success", `${planType} Clone`, "Validate successfully");
        setCloneType(planType);
        setShowCloneModal(true);
      }
      else {
        emitToasterMsg("error", `${planType} Clone`, res?.errors || "Validate failed");
      }
    } catch (err) {
      emitToasterMsg("error", `${planType} Clone`, "Validate failed");
    }
  }

  const fetchSections = async (planType) => {
    try {
      const res = await callApi({
        method: "GET",
        url: API_ROUTES.Get_Sections_By_PlanType(planType)
      });
      setSectionsList(res.sections);
    } catch (error) {
      console.error("Failed to load sections", error);
      emitToasterMsg("error", "Sections", "Failed to load sections");
    }
  };
  const CheckPdfDownloadEnable = () =>
    validateAndExecute(
      API_ROUTES.GET_ENABLE_SEEMPIII_DOWNLOAD_PDF(vesselMstId),
      handleDownloadPDF,
      "PDF"
    );

  const CheckSeempIIValidations = () =>
    validateAndExecute(
      API_ROUTES.GET_ENABLE_SEEMPIII_DOWNLOAD_PDF(vesselMstId),
      handleSubmitSeempIIIMonitoringPlan,
      "SEEMPIII"
    );



  /* ------------------------- Render ------------------------------ */

  return (
    <div>
      {/* Action buttons */}
      <div className="d-flex justify-content-end">
        <>
          {userHasFeature?.(FeatureType.SEEMPIIIClone) && (
        <Button
            className="mr-2"
            onClick={() => {
              handleValidate("SEEMP III")
            }}
          >
            SEEMP III Clone
          </Button>
        )}  
          <Button
            className="mr-4"
            startIcon={<Icon icon={ICONS.DOWNLOAD} />}
            onClick={CheckPdfDownloadEnable}
          >
            Download PDF
          </Button>
          {userHasFeature?.(FeatureType.SubmitToDocuments) && (
            <Button
              className="mr-4"
              onClick={CheckSeempIIValidations}
              disabled={!canSubmitDocuments || isSubmitting}
            >
              Submit to Documents
            </Button>
          )}
        </>
      </div>

      <FormProvider {...methods}>
        <form className="activity-data">
          {/* 1. Review and update log */}
          <Accordion
            expanded={expandedMainPanel === "panel1"}
            onChange={handleMainChange("panel1")}
            className="mt-3"
          >
            <AccordionSummary className="accordion-header">
              {renderTitleWithIcon("panel1", "1. Review and update log")}
            </AccordionSummary>
            <AccordionDetails>
              <fieldset disabled={!canSubmitDocuments} style={{ border: 0, padding: 0, margin: 0 }}>
                <ReviewAndUpdateLog
                  ReviewAndUpdateLog={monitoringData?.reviewAndUpdateLogs}
                  refetchMonitoringData={fetchMonitoringData}
                  vesselMstId={vesselMstId}
                />
              </fieldset>
            </AccordionDetails>
          </Accordion>

          {/* 2. Ship particulars + CII targets/history */}
          <Accordion
            expanded={expandedMainPanel === "panel2"}
            onChange={handleMainChange("panel2")}
            className="mt-3"
          >
            <AccordionSummary className="accordion-header">
              {renderTitleWithIcon(
                "panel2",
                "2 . Required CII over the next three years, attained CII and rating over three consecutive years"
              )}
            </AccordionSummary>
            <AccordionDetails>
              <Accordion
                expanded={expandedChild === "panel2.1"}
                onChange={handleChildChange("panel2.1")}
                className="mt-3"
              >
                <AccordionSummary className="accordion-header">
                  {renderTitleWithIcon("panel2.1", "Ship Particulars")}
                </AccordionSummary>
                <AccordionDetails>
                  <fieldset disabled={!canSubmitDocuments} style={{ border: 0, padding: 0, margin: 0 }}>
                    <ShipParticularsIII
                      vesselDetails={vesselData}
                      vesselMstId={vesselMstId}
                      ApplicableCii={monitoringData}
                      monitoringData={monitoringData?.shipParticulars}
                    />
                  </fieldset>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedChild === "panel2.2"}
                onChange={handleChildChange("panel2.2")}
                className="mt-3"
              >
                <AccordionSummary className="accordion-header">
                  {renderTitleWithIcon(
                    "panel2.2",
                    "Required CII over the next three years, attained CII and rating over three consecutive years"
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  <fieldset disabled={!canSubmitDocuments} style={{ border: 0, padding: 0, margin: 0 }}>
                    <CiiTargetsActuals
                      ciiTargetsActuals={monitoringData?.ciiTargetsActuals}
                      ciiTargets={monitoringData?.ciiTargets}
                      refetchMonitoringData={fetchMonitoringData}
                      dropdownData={allDropdownData}
                      vesselMstId={vesselMstId}
                    />
                  </fieldset>
                </AccordionDetails>
              </Accordion>
            </AccordionDetails>
          </Accordion>

          {/* 3. Calculation methodology */}
          <Accordion
            expanded={expandedMainPanel === "panel3"}
            onChange={handleMainChange("panel3")}
            className="mt-3"
          >
            <AccordionSummary className="accordion-header">
              {renderTitleWithIcon(
                "panel3",
                "3. Calculation methodology of the ship's attained annual CII, including required data and how to obtain these data as far as not addressed in part II"
              )}
            </AccordionSummary>
            <AccordionDetails>
              <fieldset disabled={!canSubmitDocuments} style={{ border: 0, padding: 0, margin: 0 }}>
                <CiiCalculationMethodology
                  Ciimethodology={monitoringData?.ciiCalculationdescription}
                  vesselMstId={vesselMstId}
                  refetchMonitoringData={fetchMonitoringData}
                />
              </fieldset>
            </AccordionDetails>
          </Accordion>

          {/* 4. Three-year implementation plan */}
          <Accordion
            expanded={expandedMainPanel === "panel4"}
            onChange={handleMainChange("panel4")}
            className="mt-3"
          >
            <AccordionSummary className="accordion-header">
              {renderTitleWithIcon("panel4", "4.Three-year implementation plan")}
            </AccordionSummary>
            <AccordionDetails>
              <fieldset disabled={!canSubmitDocuments} style={{ border: 0, padding: 0, margin: 0 }}>
                <ThreeyearImplementation
                  Threeyearplan={monitoringData?.threeyearimplementationDescription}
                  vesselMstId={vesselMstId}
                  refetchMonitoringData={fetchMonitoringData}
                />
              </fieldset>

              <Accordion
                expanded={expandedChild === "panel4.4"}
                onChange={handleChildChange("panel4.4")}
                className="mt-3"
              >
                <AccordionSummary className="accordion-header">
                  {renderTitleWithIcon(
                    "panel4.4",
                    "i.Company personnel to be responsible for the three-year implementation plan,monitoring and recording performance"
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  <fieldset disabled={!canSubmitDocuments} style={{ border: 0, padding: 0, margin: 0 }}>
                    <ThreeYearImplementationPlan
                      threeyearresponsibleplan={
                        monitoringData?.threeYearResponsiblePersonDescription
                      }
                      vesselMstId={vesselMstId}
                      refetchMonitoringData={fetchMonitoringData}
                    />
                  </fieldset>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedChild === "panel4.5"}
                onChange={handleChildChange("panel4.5")}
                className="mt-3"
              >
                <AccordionSummary className="accordion-header">
                  {renderTitleWithIcon("panel4.5", "ii.List of measures to be considered and implemented")}
                </AccordionSummary>
                <AccordionDetails>
                  <fieldset disabled={!canSubmitDocuments} style={{ border: 0, padding: 0, margin: 0 }}>
                    <ListMeasures
                      listmeasures={monitoringData?.listmeasuresDescription}
                      vesselMstId={vesselMstId}
                      refetchMonitoringData={fetchMonitoringData}
                    />
                    <ListMeasuresTable
                      listMeasures={monitoringData?.measurePlans}
                      vesselMstId={vesselMstId}
                      refetchMonitoringData={fetchMonitoringData}
                      createDate={monitoringData?.createdDate}
                    />
                  </fieldset>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedChild === "panel4.3"}
                onChange={handleChildChange("panel4.3")}
                className="mt-3"
              >
                <AccordionSummary className="accordion-header">
                  {renderTitleWithIcon(
                    "panel4.3",
                    "iii.Calculation showing the combined effect of the measures and that the required operational CII will be achieved "
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  <fieldset disabled={!canSubmitDocuments} style={{ border: 0, padding: 0, margin: 0 }}>
                    <CiiImpactCalculation
                      ciiimpactcalculation={monitoringData?.ciiImpactcalculationDescription}
                      vesselMstId={vesselMstId}
                      refetchMonitoringData={fetchMonitoringData}
                    />
                  </fieldset>
                </AccordionDetails>
                <AccordionDetails>
                  <fieldset disabled={!canSubmitDocuments} style={{ border: 0, padding: 0, margin: 0 }}>
                    <CiiTargets
                      ciiTargets={monitoringData?.ciiTargets}
                      refetchMonitoringData={fetchMonitoringData}
                      shouldRefetch={expandedChild === "panel4.3"}
                      dropdownData={allDropdownData}
                      vesselMstId={vesselMstId}
                    />
                  </fieldset>
                </AccordionDetails>
              </Accordion>
            </AccordionDetails>
          </Accordion>

          {/* 5. Self-evaluation */}
          <Accordion
            expanded={expandedMainPanel === "panel5"}
            onChange={handleMainChange("panel5")}
            className="mt-3"
          >
            <AccordionSummary className="accordion-header">
              {renderTitleWithIcon("panel5", "5. Self-evaluation and improvement")}
            </AccordionSummary>
            <AccordionDetails>
              <fieldset disabled={!canSubmitDocuments} style={{ border: 0, padding: 0, margin: 0 }}>
                <SelfEvaluationImprovement
                  selfevaluation={monitoringData?.selfevaluationDescription}
                  vesselMstId={vesselMstId}
                  refetchMonitoringData={fetchMonitoringData}
                />
              </fieldset>
            </AccordionDetails>
          </Accordion>

          {/* 6. Corrective actions */}
          <Accordion
            expanded={expandedMainPanel === "parentPanel6"}
            onChange={handleMainChange("parentPanel6")}
            className="mt-3"
          >
            <AccordionSummary className="accordion-header">
              {renderTitleWithIcon("parentPanel6", "6. Plan of corrective actions (if applicable)")}
            </AccordionSummary>
            <AccordionDetails>
              <fieldset disabled={!canSubmitDocuments} style={{ border: 0, padding: 0, margin: 0 }}>
                <PlanOfCorrectiveActions
                  planofcorrectiveness={monitoringData?.plancorrectiveDescription}
                  vesselMstId={vesselMstId}
                  refetchMonitoringData={fetchMonitoringData}
                />
              </fieldset>

              <Accordion
                expanded={expandedChild === "panel6.2"}
                onChange={handleChildChange("panel6.2")}
                className="mt-3"
              >
                <AccordionSummary className="accordion-header">
                  {renderTitleWithIcon("panel6.2", "i.Analysis of causes for inferior CII rating")}
                </AccordionSummary>
                <AccordionDetails>
                  <fieldset disabled={!canSubmitDocuments} style={{ border: 0, padding: 0, margin: 0 }}>
                    <CiiRating
                      ciirating={monitoringData?.ciiRatingDescription}
                      refetchMonitoringData={fetchMonitoringData}
                      vesselMstId={vesselMstId}
                    />
                  </fieldset>
                </AccordionDetails>
                <AccordionDetails>
                  <fieldset disabled={!canSubmitDocuments} style={{ border: 0, padding: 0, margin: 0 }}>
                    <CauseAnalysisCiiRating
                      causeAnalysisCiiRating={monitoringData?.causeAnalysisCiiRating}
                      refetchMonitoringData={fetchMonitoringData}
                      vesselMstId={vesselMstId}
                    />
                  </fieldset>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedChild === "panel6.3"}
                onChange={handleChildChange("panel6.3")}
                className="mt-3"
              >
                <AccordionSummary className="accordion-header">
                  {renderTitleWithIcon("panel6.3", "ii.Analysis of measures in the implementation plan")}
                </AccordionSummary>
                <AccordionDetails>
                  <fieldset disabled={!canSubmitDocuments} style={{ border: 0, padding: 0, margin: 0 }}>
                    <MeasureImplementationplan
                      implementationdescription={monitoringData?.implementationDescription}
                      vesselMstId={vesselMstId}
                      refetchMonitoringData={fetchMonitoringData}
                    />
                  </fieldset>
                </AccordionDetails>
                <AccordionDetails>
                  <fieldset disabled={!canSubmitDocuments} style={{ border: 0, padding: 0, margin: 0 }}>
                    <CorrectiveActionAnalysis
                      correctiveActionAnalysis={monitoringData?.correctiveActionAnalysis}
                      refetchMonitoringData={fetchMonitoringData}
                      vesselMstId={vesselMstId}
                    />
                  </fieldset>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedChild === "panel6.4"}
                onChange={handleChildChange("panel6.4")}
                className="mt-3"
              >
                <AccordionSummary className="accordion-header">
                  {renderTitleWithIcon(
                    "panel6.3",
                    "iii. List of additional measures and revised measures to be added to the implementation plan"
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  <AccordionDetails>
                    <fieldset disabled={!canSubmitDocuments} style={{ border: 0, padding: 0, margin: 0 }}>
                      <AdditionalAndRevisedMeasures
                        additionalmeasures={monitoringData?.additionalAndRevisedMeasures}
                        refetchMonitoringData={fetchMonitoringData}
                        vesselMstId={vesselMstId}
                      />
                      <AdditionalAndRevisedMeasuresTable
                        additionalMeasures={monitoringData?.additionalMeasures}
                        vesselMstId={vesselMstId}
                        refetchMonitoringData={fetchMonitoringData}
                        createDate={monitoringData?.createdDate}
                      />
                    </fieldset>
                  </AccordionDetails>
                </AccordionDetails>
              </Accordion>
            </AccordionDetails>
          </Accordion>

          {/* 7. Appendix */}
          <Accordion
            expanded={expandedMainPanel === "panel7"}
            onChange={handleMainChange("panel7")}
            className="mt-3"
          >
            <AccordionSummary className="accordion-header">
              {renderTitleWithIcon("panel7", "Appendix 1")}
            </AccordionSummary>
            <AccordionDetails>
              <fieldset disabled={!canSubmitDocuments} style={{ border: 0, padding: 0, margin: 0 }}>
                <Appendix
                  appendix={monitoringData?.appendixDescription}
                  refetchMonitoringData={fetchMonitoringData}
                  vesselMstId={vesselMstId}
                />
              </fieldset>
            </AccordionDetails>
          </Accordion>
        </form>
      </FormProvider>
      <div className="seemp-clone-modal-popup">
        <CloneModal
          isOpen={showCloneModal}
          headerTitle={"SEEMP III Clone"}
          vesselMstId={vesselMstId}
          onClose={() => {
            setShowCloneModal(false);
            setSelectedSections([]);
            setSelectedVessels([]);
            setConfirmationChecked(false);
          }}
          planType={cloneType}
          sectionsList={sectionsList || []}

          selectedSections={selectedSections}
          setSelectedSections={setSelectedSections}

          selectedVessels={selectedVessels}
          setSelectedVessels={setSelectedVessels}

          confirmationChecked={confirmationChecked}
          setConfirmationChecked={setConfirmationChecked}

          onClone={async () => {
            const payload = {
              "planType": cloneType,
              "vesselMstId": vesselMstId,
              "cloningOptionIds": [
                ...selectedSections
              ],
              "targetVesselMstIds": selectedVessels?.map(v => v.vesselMstId),
            }
            // API Call Here
            try {
              const res = await callApi({
                method: "POST",
                url: API_ROUTES.CLONE_PLAN(),
                data: payload
              });

              if (res?.isSuccess) {
                emitToasterMsg("success", `${cloneType} Clone`, "Cloned successfully");
                setShowCloneModal(false);
                setSelectedSections([]);
                setSelectedVessels([]);
                setConfirmationChecked(false);
                setShowResultModal(true);
                setResultSummaryData(res?.cloneSummaries || []);
              }
              else {
                emitToasterMsg("error", `${cloneType} Clone`, "Clone failed");
              }
            } catch (err) {
              emitToasterMsg("error", `${cloneType} Clone`, "Clone failed");
            }
          }}
        />

      </div>

      <div className="clone-result-summary-modal-popup">
        <ResultSummaryModal
          isOpen={showResultModal}
          onClose={() => setShowResultModal(false)}
          resultSummaryData={resultSummaryData || []}
          headerTitle={"SEEMP III Clone - Result Summary"}
          subHeaderTitle={"Clone result for selected target vessels"}
          successMsg={"SEEMP III"}
        />
      </div>

    </div>
  );
};

export default SeempMonitoringPlanIII;
